/**
 * @fileoverview A aba Conhecimento do agente: o texto com as instruções e a base
 * de conhecimento (cada texto salvo vira uma versão) e, atrás do ícone, as
 * versões, com a que está no ar e a troca para uma anterior. Com alterações não
 * salvas, a troca espera (ela recarregaria o texto por cima).
 */

import React, { useState } from "react";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import { History, X } from "lucide-react";
import { toast } from "react-toastify";

import api from "../../../../services/api";
import toastError from "../../../../errors/toastError";
import { i18n } from "../../../../translate/i18n";
import useStyles from "../TriageManager/styles";
import SectionTitle from "../TriageManager/InfoTip";
import useAgentStyles from "./styles";
import { formatDate } from "../../../../utils/formatLocaleDate";

const t = (key, values) => i18n.t(`agentManager.editor.knowledge.${key}`, values);

const KnowledgeTab = ({ agent, form, onChange, readOnly, dirty, onVersionUsed }) => {
  const classes = useStyles();
  const agentClasses = useAgentStyles();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const versions = [...(agent?.versions || [])].sort((a, b) => b.versionNumber - a.versionNumber);
  const live = versions.find((version) => version.id === agent?.activeVersionId) || null;

  const activateVersion = async (version) => {
    setBusyId(version.id);
    try {
      await api.post(`/ai-agents/${agent.id}/activate-version`, { versionId: version.id });
      toast.success(t("versionUsed", { number: version.versionNumber }));
      await onVersionUsed();
    } catch (err) {
      toastError(err);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className={agentClasses.knowledge}>
      <div className={agentClasses.knowledgeMain}>
        <div className={agentClasses.knowledgeHead}>
          <SectionTitle title={t("title")} info={t("info")} />
          {agent && versions.length > 0 && (
            <button type="button" className={classes.btnGhost} onClick={() => setHistoryOpen((open) => !open)} aria-expanded={historyOpen}>
              <History /> {t("history")}
            </button>
          )}
        </div>
        <TextField
          className={`${classes.field} ${agentClasses.promptField}`}
          variant="outlined"
          multiline
          minRows={16}
          maxRows={30}
          fullWidth
          placeholder={t("placeholder")}
          value={form.prompt}
          onChange={(event) => onChange({ prompt: event.target.value })}
          disabled={readOnly}
        />
        <div className={agentClasses.note}>
          {live ? t("live", { number: live.versionNumber, date: formatDate(live.createdAt) }) : t("liveNone")}
        </div>
      </div>

      {historyOpen && (
        <aside className={agentClasses.versions} aria-label={t("historyTitle")}>
          <div className={agentClasses.versionsHead}>
            <span className={agentClasses.versionsTitle}>{t("historyTitle")}</span>
            <button type="button" className={classes.iconBtn} onClick={() => setHistoryOpen(false)} aria-label={t("closeHistory")}>
              <X />
            </button>
          </div>
          {versions.map((version) => {
            const isLive = version.id === agent.activeVersionId;
            return (
              <div key={version.id} className={agentClasses.versionItem}>
                <div className={agentClasses.versionText}>
                  <div className={agentClasses.versionName}>{t("versionName", { number: version.versionNumber })}</div>
                  <div className={agentClasses.versionMeta}>
                    {formatDate(version.createdAt)}
                    {version.createdBy?.name ? ` · ${t("by", { name: version.createdBy.name })}` : ""}
                  </div>
                </div>
                {isLive ? (
                  <span className={classes.chip}>{t("current")}</span>
                ) : (
                  !readOnly && (
                    <Tooltip title={dirty ? i18n.t("agentManager.editor.unsavedHint") : ""}>
                      <span>
                        <button type="button" className={classes.btnSecondary} onClick={() => activateVersion(version)} disabled={dirty || busyId !== null}>
                          {t("useVersion")}
                        </button>
                      </span>
                    </Tooltip>
                  )
                )}
              </div>
            );
          })}
        </aside>
      )}
    </div>
  );
};

export default KnowledgeTab;
